import { useState } from 'react';
import { signInWithEmailAndPassword, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { doc, setDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import ForgotPassword from './ForgotPassword';
import { GoogleIcon, FacebookIcon } from './CustomIcons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import logo from '../images/logo.svg';
import { colors, typography, radius, inputSx as baseInputSx, btnPrimary, glassCard } from '../theme';

// Error-aware input sx
const inputSx = (hasError) => ({
    ...baseInputSx,
    '& .MuiOutlinedInput-root': {
        ...baseInputSx['& .MuiOutlinedInput-root'],
        '& fieldset':             { borderColor: hasError ? colors.error  : colors.border },
        '&:hover fieldset':       { borderColor: hasError ? colors.error  : colors.primaryBorder },
        '&.Mui-focused fieldset': { borderColor: hasError ? colors.error  : colors.primary },
    },
    '& .MuiFormHelperText-root': { color: colors.error, fontSize: 11 },
});

const labelSx = {
    color: colors.textPrimary, fontSize: 13, mb: 0.5,
    '&.Mui-focused': { color: colors.primary },
};

const getRedirect = (userData) => {
    if (userData?.role === 'shop_owner') return `/user/${userData.shopId}/dashboard`;
    if (userData?.role === 'barber' && userData?.barberId) return `/barber/${userData.shopId}/${userData.barberId}/dashboard`;
    return '/user/dashboard';
};

const ROLE_CONFIG = {
    owner:    { icon: '✂️', label: 'Shop Owner', color: colors.primary,  border: colors.primaryBorder, subtitle: 'Sign in to manage your shop',      signupLabel: "Don't have a shop yet?",  signupLink: 'Register your shop',         signupPath: '/signup/barber', switchMsg: 'Not a shop owner?' },
    barber:   { icon: '💈', label: 'Barber',     color: colors.success,  border: colors.successBorder, subtitle: 'Sign in to manage your queue',     signupLabel: 'New barber?',              signupLink: 'Join a shop with invite code', signupPath: '/join',          switchMsg: 'Not a barber?' },
    customer: { icon: '👤', label: 'Customer',   color: colors.secondary,       border: colors.secondaryBorder,  subtitle: 'Sign in to continue booking',      signupLabel: "Don't have an account?",   signupLink: 'Sign up',                    signupPath: '/signup/user',   switchMsg: 'Not a customer?' },
};

const Signup = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { login } = useAuth();

    const roleParam = searchParams.get('role');
    const hasRole   = !!ROLE_CONFIG[roleParam];
    const role      = hasRole ? roleParam : null;
    const cfg       = hasRole ? ROLE_CONFIG[role] : null;

    const [emailError,    setEmailError]    = React.useState(false);
    const [emailErrorMsg, setEmailErrorMsg] = React.useState('');
    const [passError,     setPassError]     = React.useState(false);
    const [passErrorMsg,  setPassErrorMsg]  = React.useState('');
    const [showPass,      setShowPass]      = React.useState(false);
    const [loading,       setLoading]       = React.useState(false);
    const [open,          setOpen]          = React.useState(false);

    const validate = () => {
        const email    = document.getElementById('email');
        const password = document.getElementById('password');
        let valid = true;
        if (!email.value || !/\S+@\S+\.\S+/.test(email.value)) { setEmailError(true); setEmailErrorMsg('Please enter a valid email address.'); valid = false; }
        else { setEmailError(false); setEmailErrorMsg(''); }
        if (!password.value || password.value.length < 6) { setPassError(true); setPassErrorMsg('Password must be at least 6 characters.'); valid = false; }
        else { setPassError(false); setPassErrorMsg(''); }
        return valid;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        const email    = document.getElementById('email').value;
        const password = document.getElementById('password').value;
        setLoading(true);
        try {
            const userCred = await signInWithEmailAndPassword(auth, email, password);
            const userSnap = await getDoc(doc(db, 'users', userCred.user.uid));
            const userData = userSnap.data();
            login(userCred.user, userData?.role ?? 'user');
            navigate(getRedirect(userData));
        } catch (err) {
            setEmailError(true);
            setEmailErrorMsg('Invalid email or password.');
        }
        setLoading(false);
    };

    const handleGoogle = async () => {
        setLoading(true);
        try {
            const result   = await signInWithPopup(auth, new GoogleAuthProvider());
            const user     = result.user;
            const userSnap = await getDoc(doc(db, 'users', user.uid));
            if (userSnap.exists()) {
                const userData = userSnap.data();
                if (userData.role === 'shop_owner' && !userData.shopId) { navigate('/barber/setup'); return; }
                login(user, userData?.role ?? 'user');
                navigate(getRedirect(userData));
                return;
            }
            if (role === 'owner') {
                await setDoc(doc(db, 'users', user.uid), { email: user.email, name: user.displayName, role: 'shop_owner', shopId: null, barberId: null, createdAt: serverTimestamp() });
                navigate('/barber/setup');
            } else {
                await setDoc(doc(db, 'users', user.uid), { email: user.email, name: user.displayName, role: 'user', createdAt: serverTimestamp() });
                login(user, 'user');
                navigate('/user/dashboard');
            }
        } catch (err) { console.error(err.message); }
        setLoading(false);
    };

    return (
        <Box sx={{ width: '100%', maxWidth: 400 }}>
            {/* Mobile logo */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'center' }}>
                <img src={logo} alt="DigitalQ" style={{ width: 200, height: 'auto' }} />
            </Box>
                <Typography sx={{ fontFamily: typography.poppins, fontSize: 18, fontWeight: 700, color: colors.textPrimary, lineHeight: 1.2 , textAlign:'center'}}>
                    Digital Waiting Line
                </Typography>
            {/* Card */}
            <Box sx={{ }}>

                {/* Role badge */}
                {/* {cfg && (
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.8, bgcolor: colors.bg, border: `1px solid ${cfg.border}`, borderRadius: radius.md, px: 1.2, py: 0.4, mb: 2 }}>
                        <Typography sx={{ fontSize: 13 }}>{cfg.icon}</Typography>
                        <Typography sx={{ fontSize: 12, color: cfg.color, fontWeight: 600 }}>{cfg.label}</Typography>
                    </Box>
                )} */}

                <Typography sx={{ fontFamily: typography.poppins, fontSize: 32, fontWeight: 700, color: colors.textPrimary, mb: 0.5 , textAlign:'center'}}>
                    Welcome back!!
                </Typography>
                <Typography sx={{ fontSize: 13, color: colors.textFaint, mb: 3, textAlign:'center' }}>
                    {cfg?.subtitle ?? 'Sign in to continue'}
                </Typography>

                <Box component="form" onSubmit={handleSubmit} noValidate sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <FormControl>
                        <FormLabel htmlFor="email" sx={labelSx}>Email</FormLabel>
                        <TextField error={emailError} helperText={emailErrorMsg} id="email" type="email" placeholder="your@email.com" autoComplete="email" required fullWidth size="small" sx={inputSx(emailError)} />
                    </FormControl>

                    <FormControl>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <FormLabel htmlFor="password" sx={labelSx}>Password</FormLabel>
                            <Typography onClick={() => setOpen(true)} sx={{ fontSize: 12, color: colors.textFaint, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                                Forgot password?
                            </Typography>
                        </Box>
                        <TextField error={passError} helperText={passErrorMsg} id="password" type={showPass ? 'text' : 'password'} placeholder="••••••" autoComplete="current-password" required fullWidth size="small" sx={inputSx(passError)}
                            InputProps={{ endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowPass(s => !s)} edge="end" size="small" sx={{ color: colors.textFaintest }}>
                                        {showPass ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                    </IconButton>
                                </InputAdornment>
                            )}} />
                    </FormControl>

                    <Button type="submit" fullWidth variant="contained" disabled={loading} onClick={validate} sx={{ ...btnPrimary, fontSize: 14, py: 1.2 }}>
                        {loading ? <CircularProgress size={18} sx={{ color: colors.textPrimary }} /> : 'Sign In'}
                    </Button>

                    {cfg ? (
                        <Typography sx={{ textAlign: 'center', fontSize: 13, color: colors.textFaint}}>
                            {cfg.signupLabel}{' '}
                            <Typography component="span" onClick={() => navigate(cfg.signupPath)} sx={{ color: colors.textPrimary, cursor: 'pointer', fontSize: 13, '&:hover': { textDecoration: 'underline' } }}>
                                {cfg.signupLink}
                            </Typography>
                        </Typography>
                    ) : (
                        <Typography sx={{ textAlign: 'center', fontSize: 13, color: colors.textPrimary}}>
                            Don't have an account?{' '}
                            <Typography component="span" onClick={() => navigate('/signup/user')} sx={{ color: '#4a9eff', cursor: 'pointer', fontSize: 13, '&:hover': { textDecoration: 'underline' } }}>
                                Sign up as Customer
                            </Typography>
                        </Typography>
                    )}
                </Box>

                <Divider sx={{ borderColor: colors.primary, my: 2.5 }}>
                    <Typography sx={{ fontSize: 12, color: colors.textPrimary, px: 1 }}>or</Typography>
                </Divider>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Button fullWidth variant="text" onClick={handleGoogle} disabled={loading} startIcon={<GoogleIcon />}
                        sx={{  color: colors.textPrimary, textTransform: 'none', fontSize: 13, borderRadius: radius.md, py: 1, '&:hover': { borderColor: colors.primaryBorder, bgcolor: colors.bg } }}>
                        Continue with Google
                    </Button>
                    {/* <Button fullWidth variant="outlined" disabled startIcon={<FacebookIcon />}
                        sx={{ borderColor: colors.border, color: colors.textPrimary, textTransform: 'none', fontSize: 13, borderRadius: radius.md, py: 1 }}>
                        Facebook (Coming Soon)
                    </Button> */}
                </Box>

                {/* <Box sx={{ mt: 2.5, pt: 2.5, borderTop: `1px solid ${colors.border}`, textAlign: 'center' }}>
                    {cfg ? (
                        <Typography onClick={() => navigate('/')} sx={{ fontSize: 12, color: colors.textFaintest, cursor: 'pointer', '&:hover': { color: colors.primary } }}>
                            ← {cfg.switchMsg} Choose a different role
                        </Typography>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                            <Typography sx={{ fontSize: 12, color: colors.textFaintest }}>New here? Sign up as:</Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                                {[{ path: '/signup/user', color: '#4a9eff', label: '👤 Customer' }, { path: '/signup/barber', color: colors.primary, label: '✂️ Shop Owner' }, { path: '/join', color: colors.success, label: '💈 Barber' }].map(r => (
                                    <Typography key={r.path} component="span" onClick={() => navigate(r.path)} sx={{ fontSize: 12, color: r.color, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                                        {r.label}
                                    </Typography>
                                ))}
                            </Box>
                        </Box>
                    )}
                </Box> */}
            </Box>
            <ForgotPassword open={open} handleClose={() => setOpen(false)} />
        </Box>
    );
};

export default Signup;